/**
 * AskUserCard — Structured question card for Task Mode ask_user.
 *
 * Supports both legacy (single question + flat string options) and enhanced
 * (multi-question with structured options, multi-select, descriptions, etc.)
 * formats.  Normalises legacy input into the structured format internally.
 */

import { Check, Clock, Loader2, Send } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { VoiceInputButton } from "@/components/shared/VoiceInputButton";
import type { AskUserQuestion, TaskUserInputResponse } from "@/types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AskUserCardProps {
  /** Legacy single question text. */
  question?: string | null;
  /** Legacy flat option strings. */
  options?: string[] | null;
  /** Enhanced structured questions (takes priority over legacy props). */
  questions?: AskUserQuestion[] | null;
  /** Whether submission is in progress. */
  submitting?: boolean;
  /** Called with legacy input string when using legacy mode. */
  onSubmitLegacy?: (input: string) => void;
  /** Called with structured responses when using enhanced mode. */
  onSubmitStructured?: (responses: TaskUserInputResponse[]) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Normalise legacy props into the enhanced question format. */
function normaliseLegacy(question?: string | null, options?: string[] | null): AskUserQuestion[] {
  return [
    {
      question: question || "The agent needs additional information to continue.",
      header: "Input Required",
      options: options?.map((o) => ({ label: o, description: "" })),
      multiple: false,
      allow_custom: true,
      input_type: "text",
      required: true,
    },
  ];
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Renders a checkbox (multi) or radio (single) selection indicator. */
function SelectionIndicator({
  isMulti,
  isSelected,
}: Readonly<{
  isMulti: boolean;
  isSelected: boolean;
}>) {
  if (isMulti) {
    return (
      <span
        className={`inline-flex items-center justify-center h-4 w-4 rounded border transition-colors ${
          isSelected ? "bg-brand-primary border-brand-primary text-white" : "border-content-quaternary"
        }`}
      >
        {isSelected && <Check className="h-3 w-3" />}
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center justify-center h-4 w-4 rounded-full border-2 transition-colors ${
        isSelected ? "border-brand-primary" : "border-content-quaternary"
      }`}
    >
      {isSelected && <span className="h-2 w-2 rounded-full bg-brand-primary" />}
    </span>
  );
}

/** Returns the placeholder text for an input based on its type. */
function getInputPlaceholder(inputType: string): string {
  switch (inputType) {
    case "email":
      return "email@example.com";
    case "url":
      return "https://...";
    case "date":
      return "YYYY-MM-DD";
    default:
      return "Enter your answer...";
  }
}

/** Renders a text input or textarea for custom/open-ended answers. */
function AnswerInput({
  inputType,
  value,
  onChange,
  variant,
}: {
  inputType: string;
  value: string;
  onChange: (text: string) => void;
  variant: "surface-primary" | "surface-secondary";
}) {
  const bgClass = variant === "surface-primary" ? "bg-surface-primary" : "bg-surface-secondary";

  if (inputType === "textarea") {
    return (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter your answer..."
        rows={3}
        className={`w-full px-3 py-2 ${bgClass} border border-border-secondary rounded-lg text-sm text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary resize-none`}
      />
    );
  }
  return (
    <input
      type={inputType}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={getInputPlaceholder(inputType)}
      className={`w-full px-3 py-2 ${bgClass} border border-border-secondary rounded-lg text-sm text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary`}
    />
  );
}

interface QuestionBlockProps {
  q: AskUserQuestion;
  index: number;
  /** Currently selected option labels. */
  selected: string[];
  /** Custom free-text value. */
  customText: string;
  /** Whether "custom" option is toggled on. */
  customActive: boolean;
  onToggleOption: (index: number, label: string) => void;
  onToggleCustom: (index: number) => void;
  onCustomTextChange: (index: number, text: string) => void;
}

function QuestionBlock({
  // NOSONAR -- question rendering with options, custom input, and multi-select variants
  q,
  index,
  selected,
  customText,
  customActive,
  onToggleOption,
  onToggleCustom,
  onCustomTextChange,
}: Readonly<QuestionBlockProps>) {
  const hasOptions = q.options && q.options.length > 0;
  const isMulti = q.multiple ?? false;
  const allowCustom = q.allow_custom !== false; // default true
  const inputType = q.input_type || "text";

  return (
    <div className="rounded-lg border border-border-secondary bg-surface-primary p-4">
      {/* Header + question */}
      <h4 className="text-sm font-semibold text-content-primary mb-1">{q.header}</h4>
      <p className="text-sm text-content-secondary mb-3">{q.question}</p>

      {/* Options */}
      {hasOptions && (
        <div className="space-y-2 mb-3">
          {q.options!.map((opt) => {
            const isSelected = selected.includes(opt.label);
            return (
              <label
                key={opt.label}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-brand-primary ${
                  isSelected
                    ? "bg-brand-primary-subtle border-brand-primary"
                    : "bg-surface-secondary border-border-secondary hover:border-border-hover"
                }`}
              >
                <input
                  type={isMulti ? "checkbox" : "radio"}
                  name={`ask-user-q${index}`}
                  checked={isSelected}
                  onChange={() => onToggleOption(index, opt.label)}
                  className="fixed opacity-0 pointer-events-none"
                />
                <span className="mt-0.5 shrink-0">
                  <SelectionIndicator isMulti={isMulti} isSelected={isSelected} />
                </span>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-content-primary">{opt.label}</span>
                  {opt.description && <p className="text-xs text-content-tertiary mt-0.5">{opt.description}</p>}
                </div>
              </label>
            );
          })}

          {/* "Type your own" option */}
          {allowCustom && (
            <div
              className={`rounded-lg border transition-colors ${
                customActive
                  ? "bg-brand-primary-subtle border-brand-primary"
                  : "bg-surface-secondary border-border-secondary hover:border-border-hover"
              }`}
            >
              <label className="flex items-start gap-3 p-3 cursor-pointer focus-within:ring-2 focus-within:ring-brand-primary rounded-lg">
                <input
                  type={isMulti ? "checkbox" : "radio"}
                  name={`ask-user-q${index}`}
                  checked={customActive}
                  onChange={() => onToggleCustom(index)}
                  className="fixed opacity-0 pointer-events-none"
                />
                <span className="mt-0.5 shrink-0">
                  <SelectionIndicator isMulti={isMulti} isSelected={customActive} />
                </span>
                <span className="text-sm font-medium text-content-primary">Type your own answer...</span>
              </label>
              {/* Rendered as a sibling (not a label descendant) so typing/clicking
                  inside it can never be mistaken for re-toggling the option above. */}
              {customActive && (
                <div className="px-3 pb-3">
                  <AnswerInput
                    inputType={inputType}
                    value={customText}
                    onChange={(text) => onCustomTextChange(index, text)}
                    variant="surface-primary"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Open-ended (no options) — just a text input */}
      {!hasOptions && (
        <div>
          <AnswerInput
            inputType={inputType}
            value={customText}
            onChange={(text) => onCustomTextChange(index, text)}
            variant="surface-secondary"
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface QuestionState {
  selected: string[];
  customText: string;
  customActive: boolean;
}

export function AskUserCard({
  question,
  options,
  questions,
  submitting = false,
  onSubmitLegacy,
  onSubmitStructured,
}: AskUserCardProps) {
  // Determine which mode we're in
  const isEnhanced = !!(questions && questions.length > 0);
  const normalised = useMemo<AskUserQuestion[]>(
    () => (isEnhanced ? questions! : normaliseLegacy(question, options)),
    [isEnhanced, questions, question, options],
  );

  // Per-question state
  const [qStates, setQStates] = useState<QuestionState[]>(() =>
    normalised.map(() => ({ selected: [], customText: "", customActive: false })),
  );

  // Voice input applies to the first open-ended question or the first custom text
  const onVoiceTranscript = useCallback(
    (text: string) => {
      // Find first question that accepts free text
      const idx = normalised.findIndex((q, i) => !q.options || q.options.length === 0 || qStates[i]?.customActive);
      if (idx >= 0) {
        setQStates((prev) => {
          const next = [...prev];
          next[idx] = {
            ...next[idx],
            customText: next[idx].customText ? `${next[idx].customText} ${text}` : text,
            customActive: true,
          };
          return next;
        });
      }
    },
    [normalised, qStates],
  );

  const handleToggleOption = useCallback(
    (qIndex: number, label: string) => {
      setQStates((prev) => {
        const next = [...prev];
        const q = normalised[qIndex];
        const isMulti = q.multiple ?? false;
        const current = next[qIndex].selected;

        if (isMulti) {
          // Toggle in multi-select
          next[qIndex] = {
            ...next[qIndex],
            selected: current.includes(label) ? current.filter((l) => l !== label) : [...current, label],
          };
        } else {
          // Single-select: replace
          next[qIndex] = {
            ...next[qIndex],
            selected: current.includes(label) ? [] : [label],
            customActive: false,
            customText: "",
          };
        }
        return next;
      });
    },
    [normalised],
  );

  const handleToggleCustom = useCallback(
    (qIndex: number) => {
      setQStates((prev) => {
        const next = [...prev];
        const q = normalised[qIndex];
        const isMulti = q.multiple ?? false;

        if (isMulti) {
          next[qIndex] = {
            ...next[qIndex],
            customActive: !next[qIndex].customActive,
          };
        } else {
          // Single-select: toggle custom, deselect options
          const wasActive = next[qIndex].customActive;
          next[qIndex] = {
            ...next[qIndex],
            customActive: !wasActive,
            selected: [],
            customText: wasActive ? "" : next[qIndex].customText,
          };
        }
        return next;
      });
    },
    [normalised],
  );

  const handleCustomTextChange = useCallback((qIndex: number, text: string) => {
    setQStates((prev) => {
      const next = [...prev];
      next[qIndex] = { ...next[qIndex], customText: text };
      return next;
    });
  }, []);

  // Validate: all required questions must have an answer
  const canSubmit = useMemo(() => {
    return normalised.every((q, i) => {
      const st = qStates[i];
      if (!st) return false;
      const isRequired = q.required !== false;
      if (!isRequired) return true;

      const hasOptions = q.options && q.options.length > 0;
      if (hasOptions) {
        return st.selected.length > 0 || (st.customActive && st.customText.trim().length > 0);
      }
      // Open-ended
      return st.customText.trim().length > 0;
    });
  }, [normalised, qStates]);

  const handleSubmit = useCallback(() => {
    if (!canSubmit || submitting) return;

    if (!isEnhanced && onSubmitLegacy) {
      // Legacy mode: send plain text
      const st = qStates[0];
      const text = st.selected.length > 0 ? st.selected[0] : st.customText.trim();
      onSubmitLegacy(text);
      return;
    }

    if (onSubmitStructured) {
      const responses: TaskUserInputResponse[] = normalised.map((q, i) => {
        const st = qStates[i];
        const hasOptions = q.options && q.options.length > 0;
        // For questions with options, only include customText if the custom
        // input was explicitly activated. For open-ended questions (no options),
        // always include the text.
        const includeCustomText = hasOptions ? st.customActive && !!st.customText.trim() : !!st.customText.trim();
        return {
          header: q.header,
          question: q.question,
          selected: st.selected,
          customText: includeCustomText ? st.customText.trim() : undefined,
        };
      });
      onSubmitStructured(responses);
      return;
    }

    // Fallback: use legacy with concatenated answers
    if (onSubmitLegacy) {
      const parts = normalised.map((q, i) => {
        const st = qStates[i];
        const answer = st.selected.length > 0 ? st.selected.join(", ") : st.customText.trim();
        return `${q.header}: ${answer}`;
      });
      onSubmitLegacy(parts.join("\n"));
    }
  }, [canSubmit, submitting, isEnhanced, normalised, qStates, onSubmitLegacy, onSubmitStructured]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Only submit on Enter if there's a single question with no options
      if (
        e.key === "Enter" &&
        !e.shiftKey &&
        normalised.length === 1 &&
        (!normalised[0].options || normalised[0].options.length === 0)
      ) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [normalised, handleSubmit],
  );

  return (
    <form // NOSONAR - onKeyDown handles Ctrl+Enter form submission
      className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6"
      onSubmit={(e) => e.preventDefault()}
      onKeyDown={handleKeyDown}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <Clock className="h-5 w-5 text-yellow-400" />
        <div>
          <h3 className="font-medium text-content-primary">Input Required</h3>
          {normalised.length > 1 && (
            <p className="text-sm text-content-tertiary mt-0.5">
              Please answer {normalised.length} question{normalised.length > 1 ? "s" : ""} below.
            </p>
          )}
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-4 mb-4">
        {normalised.map((q, i) => (
          <QuestionBlock
            key={q.question}
            q={q}
            index={i}
            selected={qStates[i]?.selected ?? []}
            customText={qStates[i]?.customText ?? ""}
            customActive={qStates[i]?.customActive ?? false}
            onToggleOption={handleToggleOption}
            onToggleCustom={handleToggleCustom}
            onCustomTextChange={handleCustomTextChange}
          />
        ))}
      </div>

      {/* Submit bar */}
      <div className="flex items-center justify-end gap-3">
        <VoiceInputButton onTranscript={onVoiceTranscript} disabled={submitting} size="sm" />
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting || !canSubmit}
          className="flex items-center gap-2 px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {normalised.length > 1 ? "Submit All" : "Submit"}
        </button>
      </div>
    </form>
  );
}
